import {
  Box,
  Button,
  LinearProgress,
  Stack,
  Typography,
  alpha,
} from "@mui/material";
import {
  CloudUploadRounded,
  DeleteRounded,
  InsertDriveFileRounded,
} from "@mui/icons-material";
import { useCallback, useRef, useState } from "react";
import { uploadBenefitDocument } from "../api/tenant-documents";

type BenefitDocumentUploadProps = {
  token: string;
  onUploadComplete: () => void;
};

export function BenefitDocumentUpload({
  token,
  onUploadComplete,
}: BenefitDocumentUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const ALLOWED_TYPES = [
    "image/png",
    "image/jpeg",
    "image/webp",
    "application/pdf",
  ];

  const validateFile = (file: File): boolean => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      setError(
        "Invalid file type. Please upload a PNG, JPEG, WebP image or PDF.",
      );
      return false;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError("File size must be less than 10MB.");
      return false;
    }
    setError("");
    return true;
  };

  const handleFileSelect = (file: File) => {
    if (validateFile(file)) {
      setSelectedFile(file);
    }
  };

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setIsDragging(false);
      const file = event.dataTransfer.files[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [],
  );

  const handleDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setIsDragging(true);
    },
    [],
  );

  const handleDragLeave = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setIsDragging(false);
    },
    [],
  );

  const handleUpload = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    setError("");

    try {
      await uploadBenefitDocument(token, selectedFile);
      setSelectedFile(null);
      onUploadComplete();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to upload document",
      );
    } finally {
      setIsUploading(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Stack spacing={2}>
      <Box
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => fileInputRef.current?.click()}
        sx={{
          border: "2px dashed",
          borderColor: isDragging ? "primary.main" : "divider",
          borderRadius: 3,
          p: 4,
          textAlign: "center",
          cursor: "pointer",
          transition: "all 0.2s ease",
          bgcolor: isDragging
            ? (theme) => alpha(theme.palette.primary.main, 0.04)
            : "transparent",
          "&:hover": {
            borderColor: "primary.main",
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.02),
          },
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".png,.jpg,.jpeg,.webp,.pdf"
          style={{ display: "none" }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFileSelect(file);
            e.target.value = "";
          }}
        />
        <CloudUploadRounded
          sx={{ fontSize: 48, color: "primary.main", mb: 1 }}
        />
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Drop your file here or click to browse
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          PNG, JPEG, WebP, or PDF — max 10MB
        </Typography>
      </Box>

      {selectedFile && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            p: 1.5,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "grey.50",
          }}
        >
          <InsertDriveFileRounded color="primary" />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {selectedFile.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatFileSize(selectedFile.size)}
            </Typography>
          </Box>
          <Button
            size="small"
            color="error"
            onClick={() => setSelectedFile(null)}
            startIcon={<DeleteRounded />}
            sx={{ textTransform: "none" }}
          >
            Remove
          </Button>
        </Box>
      )}

      {isUploading && (
        <Box>
          <LinearProgress sx={{ borderRadius: 6 }} />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
            Uploading...
          </Typography>
        </Box>
      )}

      {error && (
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      )}

      <Button
        variant="contained"
        onClick={handleUpload}
        disabled={!selectedFile || isUploading}
        startIcon={<CloudUploadRounded />}
        sx={{
          borderRadius: 2,
          py: 1.2,
          fontWeight: 600,
          boxShadow: "none",
          "&:hover": { boxShadow: "none" },
        }}
      >
        Upload Document
      </Button>
    </Stack>
  );
}
